import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";

/**
 * EVA Textarea - Composant 100% custom
 *
 * Zone de texte multi-lignes utilisant les tokens EVA (pas de PrimeNG).
 * Supporte label, helper text, error state, resize et compteur de caractères.
 *
 * @example
 * // Textarea simple
 * <eva-textarea label="Description" [(value)]="description"></eva-textarea>
 *
 * // Textarea avec compteur et validation
 * <eva-textarea
 *   label="Notes"
 *   [maxlength]="500"
 *   [showCount]="true"
 *   error="Ce champ est obligatoire"
 * ></eva-textarea>
 *
 * // Textarea non redimensionnable
 * <eva-textarea label="Commentaire" resize="none" [rows]="4"></eva-textarea>
 */
@Component({
  selector: "eva-textarea",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./eva-textarea.component.html",
  styleUrls: ["./eva-textarea.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaTextareaComponent),
      multi: true,
    },
  ],
})
export class EvaTextareaComponent implements ControlValueAccessor {
  // ==========================================================================
  // Native textarea Inputs
  // ==========================================================================

  /** Placeholder */
  @Input() placeholder: string = "";

  /** Désactive le textarea */
  @Input() disabled: boolean = false;

  /** Textarea en lecture seule */
  @Input() readonly: boolean = false;

  /** Longueur maximale */
  @Input() maxlength: number | null = null;

  /** Nombre de lignes visibles */
  @Input() rows: number = 3;

  /** Comportement de redimensionnement */
  @Input() resize: "none" | "vertical" | "horizontal" | "both" = "vertical";

  /** Autocomplete */
  @Input() autocomplete: string = "off";

  // ==========================================================================
  // EVA-specific Inputs
  // ==========================================================================

  /** Label affiché au-dessus du textarea */
  @Input() label: string = "";

  /** Texte d'aide affiché sous le textarea (masqué si error est présent) */
  @Input() helper: string = "";

  /** Message d'erreur (affiché en rouge, remplace helper) */
  @Input() error: string = "";

  /** Ajoute un * au label */
  @Input() required: boolean = false;

  /** Affiche le compteur de caractères */
  @Input() showCount: boolean = false;

  /** ID unique pour le textarea (auto-généré si non fourni) */
  @Input() inputId: string = `eva-textarea-${Math.random().toString(36).substr(2, 9)}`;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand la valeur change — pour le two-way binding [(value)] */
  @Output() valueChange = new EventEmitter<string>();
  /** Émis quand le textarea reçoit le focus */
  @Output() onFocus = new EventEmitter<FocusEvent>();
  /** Émis quand le textarea perd le focus */
  @Output() onBlur = new EventEmitter<FocusEvent>();

  // ==========================================================================
  // Internal state
  // ==========================================================================

  _value: string = "";
  isFocused: boolean = false;

  // ==========================================================================
  // Two-way binding [(value)]
  // ==========================================================================

  @Input()
  get value(): string {
    return this._value;
  }
  set value(val: string) {
    if (val !== this._value) {
      this._value = val;
      this.onChange(val);
      this.valueChange.emit(val);
    }
  }

  // ==========================================================================
  // ControlValueAccessor
  // ==========================================================================

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string): void {
    this._value = value || "";
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  /**
   * Texte du compteur de caractères
   */
  get characterCount(): string {
    const current = this._value?.length || 0;
    if (this.maxlength) {
      return `${current} / ${this.maxlength}`;
    }
    return `${current}`;
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this._value = textarea.value;
    this.onChange(this._value);
    this.valueChange.emit(this._value);
  }

  handleFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.onFocus.emit(event);
  }

  handleBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.onBlur.emit(event);
  }
}
